import { defineStore } from 'pinia';

interface MetaAttributes {
  [key: string]: string;
}

interface State {
  fonts: string[];
  bodyClassList: string[];
  userAgent: string | null;
  host: string | null;
  path: string | null;
  cookies: string | null;
  overwrittenWindowHeight: number | null;
  overwrittenUrl: string | null;
  campaignPadding: number | null;
  metatags: MetaAttributes[];
}

export const useUtilityStore = defineStore('utility', {
  state: (): State => ({
    fonts: [],
    bodyClassList: [],
    userAgent: null,
    host: null,
    path: null,
    cookies: null,
    overwrittenWindowHeight: null,
    overwrittenUrl: null,
    campaignPadding: 0,
    metatags: []
  }),
  getters: {
    windowHeight(state) {
      return state.overwrittenWindowHeight ?? window.innerHeight;
    },
    url(state) {
      if (state.overwrittenUrl === null) {
        return '';
      }

      return state.overwrittenUrl;
    },

    isDemoUrl(state) {
      let url = state.overwrittenUrl;
      if (!url) {
        url = window.location.href;
      }

      if (url) {
        return url.includes('/campaign/view/demo');
      }
      return false;
    }
  },

  actions: {
    addBodyClass(className: string) {
      this.addBodyClasses([className]);
    },
    addBodyClasses(classList: string[]) {
      classList.forEach((className) => {
        if (!this.bodyClassList.includes(className)) {
          this.bodyClassList.push(className);
        }
      });
    },
    removeBodyClass(className: string) {
      this.removeBodyClasses([className]);
    },
    removeBodyClasses(classList: string[]) {
      classList.forEach((className) => {
        if (this.bodyClassList.includes(className)) {
          this.bodyClassList.splice(this.bodyClassList.indexOf(className), 1);
        }
      });
    },
    addRemoveClassList(addClasses: string[], removeClasses: string[]) {
      addClasses.forEach((className) => {
        if (!this.bodyClassList.includes(className)) {
          this.bodyClassList.push(className);
        }
      });

      removeClasses.forEach((className) => {
        if (this.bodyClassList.includes(className)) {
          this.bodyClassList.splice(this.bodyClassList.indexOf(className), 1);
        }
      });
    },
    addMetatag(metaAttributes: MetaAttributes) {
      const existingEntry = this.metatags.find((tag) => {
        for (const key in tag) {
          if (Object.prototype.hasOwnProperty.call(tag, key)) {
            if (tag[`${key}`] === metaAttributes[`${key}`]) {
              return metaAttributes;
            }
          }
        }

        return false;
      });

      if (!existingEntry) {
        this.metatags.push(metaAttributes);
      } else {
        this.metatags.push(existingEntry);
      }
    },
    loadFont(fontFamily: string) {
      const isCustom = fontFamily.includes('custom:');
      if (isCustom) return;

      // Sometimes we might get font family forwarded with multiple fonts in them.
      // Here we need to destruct the font family using regex and find our font we need to get.
      if (fontFamily.includes('"')) {
        const match = fontFamily.match(/"([a-z_\-0-9 ]+)"/i);

        if (match && match[1]) {
          fontFamily = match[1];
        }
      }

      if (!this.fonts.includes(fontFamily)) {
        this.fonts.push(fontFamily);
      }
    }
  }
});
